/**
 * DOM-based tab navigation for recap table cells.
 *
 * Navigable cells get `data-tab-cell` attribute.
 * The table wrapper gets `data-recap-table` attribute.
 * Hidden columns are auto-skipped because they're not in DOM.
 */

function getTabCells(el: HTMLElement): HTMLElement[] {
  const table = el.closest("[data-recap-table]");
  if (!table) return [];
  return Array.from(table.querySelectorAll<HTMLElement>("[data-tab-cell]"));
}

/**
 * A tab cell that belongs to a hidden column must not catch Tab focus —
 * the user only ever sees (and should only land on) columns that render.
 *
 * Most hidden columns are dropped from the DOM entirely by `useRecapColumns`,
 * so they never appear here. This guard covers the remaining case: a cell
 * whose column (or an ancestor wrapper) is hidden via CSS — `display:none`,
 * `visibility:hidden`, or the `hidden` attribute. Without it, a CSS-hidden
 * column would still sit in the `data-tab-cell` traversal and steal an
 * invisible Tab stop between two visible cells.
 *
 * Layout-based checks (`getBoundingClientRect`, `offsetParent`) are avoided
 * on purpose: jsdom reports no layout, so they would flag every cell as
 * hidden in tests. Computed `display`/`visibility` is reliable in both.
 */
function isTabCellHidden(el: HTMLElement): boolean {
  if (globalThis.window === undefined) return false;
  let node: HTMLElement | null = el;
  while (node && node.dataset.recapTable === undefined) {
    if (node.hidden) return true;
    const style = globalThis.getComputedStyle(node);
    if (style.display === "none" || style.visibility === "hidden") return true;
    node = node.parentElement;
  }
  return false;
}

function findCurrentIndex(cells: HTMLElement[], el: HTMLElement): number {
  let idx = cells.indexOf(el);
  if (idx !== -1) return idx;

  const parentCell = el.closest<HTMLElement>("[data-tab-cell]");
  if (parentCell) {
    idx = cells.indexOf(parentCell);
  }
  return idx;
}

/**
 * Capture the table element and current cell index BEFORE any DOM changes.
 * Use this before flushSync/save operations that unmount the current element.
 */
export function captureTabPosition(
  el: HTMLElement,
): { table: HTMLElement; index: number } | null {
  const table = el.closest<HTMLElement>("[data-recap-table]");
  if (!table) return null;
  const cells = Array.from(
    table.querySelectorAll<HTMLElement>("[data-tab-cell]"),
  );
  const idx = findCurrentIndex(cells, el);
  if (idx === -1) return null;
  return { table, index: idx };
}

/**
 * Focus a tab cell by index within a table.
 * Use after DOM changes with a previously captured position.
 */
export function focusTabCellAt(table: HTMLElement, index: number): void {
  const cells = Array.from(
    table.querySelectorAll<HTMLElement>("[data-tab-cell]"),
  );
  const target = cells[index];
  if (target) target.focus();
}

export function focusNextTabCell(el: HTMLElement): boolean {
  const cells = getTabCells(el);
  const idx = findCurrentIndex(cells, el);
  if (idx === -1 || cells.length === 0) return false;

  for (let i = idx + 1; i < cells.length; i++) {
    if (!isTabCellHidden(cells[i])) {
      cells[i].focus();
      return true;
    }
  }
  return false;
}

export function focusPrevTabCell(el: HTMLElement): boolean {
  const cells = getTabCells(el);
  const idx = findCurrentIndex(cells, el);
  if (idx === -1 || cells.length === 0) return false;

  for (let i = idx - 1; i >= 0; i--) {
    if (!isTabCellHidden(cells[i])) {
      cells[i].focus();
      return true;
    }
  }
  return false;
}

/**
 * Focus the same column one row down — the Enter behaviour for editable
 * cells. "Same column" is matched on the cell's `data-col-id` <td>, so a
 * value typed in row N's quantity lands in row N+1's quantity, mirroring a
 * spreadsheet. Rows that don't render that column (or render it hidden) are
 * skipped. Returns false when there is no next row with that column — the
 * caller then falls back to the new-product row (the table's last line).
 */
export function focusNextRowSameColumn(el: HTMLElement): boolean {
  const td = el.closest<HTMLElement>("td[data-col-id]");
  const row = el.closest<HTMLElement>("tr");
  const colId = td?.dataset.colId;
  if (!td || !row || !colId) return false;

  let next = row.nextElementSibling as HTMLElement | null;
  while (next) {
    const cell = findColumnTabCell(next, colId);
    if (cell && !isTabCellHidden(cell)) {
      cell.focus();
      return true;
    }
    next = next.nextElementSibling as HTMLElement | null;
  }
  return false;
}

/**
 * Find the focusable tab cell of a given column within a row, matching on the
 * `<td>`'s `data-col-id`. Avoids building a dynamic CSS selector from the id.
 */
function findColumnTabCell(
  row: HTMLElement,
  colId: string,
): HTMLElement | null {
  const tds = Array.from(row.querySelectorAll<HTMLElement>("td[data-col-id]"));
  const match = tds.find((td) => td.dataset.colId === colId);
  return match?.querySelector<HTMLElement>("[data-tab-cell]") ?? null;
}

/**
 * Shared Enter / Shift+Enter target for editable cells. Shift+Enter jumps to
 * the new-product row (the table's last line); plain Enter steps to the same
 * column in the next row, falling back to the new row on the last data row.
 */
export function focusEnterTarget(el: HTMLElement, shiftKey: boolean): void {
  if (!shiftKey && focusNextRowSameColumn(el)) return;
  focusNewRowSku(el);
}

/**
 * Focus the first tab cell in the new (empty) product row.
 * This is the SKU input where users add new products.
 */
export function focusNewRowSku(el: HTMLElement): void {
  const table = el.closest("[data-recap-table]");
  if (!table) return;
  const newRow = table.querySelector<HTMLElement>("[data-new-product-row]");
  if (!newRow) return;
  const firstCell = newRow.querySelector<HTMLElement>("[data-tab-cell]");
  if (firstCell) firstCell.focus();
}
